import { useEffect, useRef, useState } from 'react';
import { isDesktop } from 'react-device-detect';
import Icon from 'components/icons';
import { Spinner } from 'components/loadings';
import type { ICustomContentVisibility } from 'services/customContent';
import { ICustomContentCardProps } from '../types';
import {
  Added,
  Card,
  CardContent,
  ContentTypeTag,
  Description,
  DetailValue,
  Divider,
  MetricsColumn,
  Tags,
  Title,
  VisibilityButton,
  VisibilityButtonChevron,
  VisibilityDescription,
  VisibilityDropdown,
  VisibilityOption,
  VisibilityOptionContent,
  VisibilityOptionIndicator,
  VisibilityOptionLabel,
  VisibilityReadOnlyPill,
  VisibilitySection,
} from './CustomContentCard.styles';
import CustomContentMetrics from './CustomContentMetrics';

const toVisibilityLabel = (visibility: ICustomContentVisibility): string =>
  visibility === 'limited' ? 'Limited' : 'Unlimited';

const CustomContentCard = ({
  item,
  loading = false,
  metrics,
  metricsLoading = false,
  showMetrics = true,
  metricsTitle,
  canViewVisibility = false,
  canManageVisibility = false,
  onVisibilityChange,
  isVisibilityPending = false,
}: ICustomContentCardProps): JSX.Element => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const visibilitySectionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (
        visibilitySectionRef.current &&
        !visibilitySectionRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  if (loading) {
    return (
      <Card>
        <Spinner active size="40" top="50%" left="50%" />
      </Card>
    );
  }

  if (!item) {
    return null;
  }

  const shouldRenderMetrics = Boolean(showMetrics && !metricsLoading && metrics?.length);

  const handleSelectVisibility = async (visibility: ICustomContentVisibility) => {
    if (!onVisibilityChange || isVisibilityPending || visibility === item.visibility) {
      setIsDropdownOpen(false);
      return;
    }

    await onVisibilityChange(item.id, visibility);
    setIsDropdownOpen(false);
  };

  const { contentTypeVisual } = item;
  const showVisibility = canViewVisibility || canManageVisibility;
  const visibilityMenuId = `visibility-menu-${item.id}`;
  const renderVisibilitySection = () => {
    if (!showVisibility) return null;

    return (
      <VisibilitySection ref={visibilitySectionRef}>
        {canManageVisibility ? (
          <>
            <VisibilityButton
              $isOpen={isDropdownOpen}
              type="button"
              onClick={() => setIsDropdownOpen((previous) => !previous)}
              disabled={isVisibilityPending}
              aria-expanded={isDropdownOpen}
              aria-haspopup="menu"
              aria-controls={visibilityMenuId}
            >
              <span>Visibility: {toVisibilityLabel(item.visibility)}</span>
              <VisibilityButtonChevron aria-hidden>
                <Icon
                  icon="ChevronDown"
                  color="var(--light-gray-darker, #57575c)"
                  style={{ width: '9.333px', height: '5.333px' }}
                />
              </VisibilityButtonChevron>
            </VisibilityButton>
            {isDropdownOpen && (
              <VisibilityDropdown id={visibilityMenuId} role="menu" aria-label="Visibility options">
                <VisibilityOption
                  type="button"
                  role="menuitemradio"
                  aria-checked={item.visibility === 'unlimited'}
                  onClick={() => handleSelectVisibility('unlimited')}
                  disabled={isVisibilityPending}
                >
                  <VisibilityOptionIndicator
                    $selected={item.visibility === 'unlimited'}
                    aria-hidden
                  />
                  <VisibilityOptionContent>
                    <VisibilityOptionLabel>Unlimited </VisibilityOptionLabel>
                    <VisibilityDescription>
                      All learners on the account can view
                    </VisibilityDescription>
                  </VisibilityOptionContent>
                </VisibilityOption>
                <VisibilityOption
                  type="button"
                  role="menuitemradio"
                  aria-checked={item.visibility === 'limited'}
                  onClick={() => handleSelectVisibility('limited')}
                  disabled={isVisibilityPending}
                >
                  <VisibilityOptionIndicator
                    $selected={item.visibility === 'limited'}
                    aria-hidden
                  />
                  <VisibilityOptionContent>
                    <VisibilityOptionLabel>Limited </VisibilityOptionLabel>
                    <VisibilityDescription>
                      Only learners assigned through playlists can view
                    </VisibilityDescription>
                  </VisibilityOptionContent>
                </VisibilityOption>
              </VisibilityDropdown>
            )}
          </>
        ) : (
          <VisibilityReadOnlyPill>
            Visibility: {toVisibilityLabel(item.visibility)}
          </VisibilityReadOnlyPill>
        )}
      </VisibilitySection>
    );
  };

  return (
    <Card>
      <CardContent>
        <Title title={item.title}>{item.title}</Title>

        <Tags>
          <ContentTypeTag
            $bgColor={contentTypeVisual.backgroundColor}
            $textColor={contentTypeVisual.color}
          >
            <Icon
              icon={contentTypeVisual.icon}
              color={contentTypeVisual.color}
              style={{ width: '14px', height: '14px', aspectRatio: '1 / 1' }}
            />
            {contentTypeVisual.label}
          </ContentTypeTag>
          <DetailValue>{item.difficulty}</DetailValue>
          <Divider />
          <DetailValue>{item.duration}</DetailValue>
        </Tags>

        <Description $hasMetricsColumn={shouldRenderMetrics}>{item.description}</Description>
        <Added>{item.added}</Added>
        {isDesktop && renderVisibilitySection()}
      </CardContent>

      {shouldRenderMetrics && (
        <MetricsColumn>
          <CustomContentMetrics
            metrics={metrics}
            contentType={item.rawItem?.content_type}
            title={metricsTitle}
          />
        </MetricsColumn>
      )}
      {!isDesktop && renderVisibilitySection()}
    </Card>
  );
};

export default CustomContentCard;
